import React, { useEffect } from 'react';
import { ArrowRight, CheckCircle, MapPin } from 'lucide-react'; // Added MapPin icon
import { Link } from 'react-router-dom';

const WelcomeSection = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-active');
          }
        });
      },
      { threshold: 0.15 }
    );

    const hiddenElements = document.querySelectorAll('.reveal-on-scroll');
    hiddenElements.forEach(el => observer.observe(el));

    return () => hiddenElements.forEach(el => observer.unobserve(el));
  }, []);

  return (
    <section className="py-24 md:py-32 px-6 bg-[#FAF9F7] relative overflow-hidden font-['Inter']">

      {/* LOCAL ANIMATION STYLES */}
      <style>{`
        .reveal-on-scroll {
          opacity: 0;
          transform: translateY(40px);
          transition: all 1s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .reveal-on-scroll.animate-active {
          opacity: 1;
          transform: translateY(0);
        }
        .delay-100 { transition-delay: 0.1s; }
        .delay-200 { transition-delay: 0.2s; }
        .delay-300 { transition-delay: 0.3s; }

        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-text {
          animation: spin-slow 12s linear infinite;
        }
      `}</style>

      {/* Background Text - Keeps the Brand Feel */}
      <div className="absolute top-10 left-0 w-full text-center pointer-events-none select-none">
        <h1 className="text-[10rem] md:text-[14rem] font-serif text-gray-100 opacity-40 whitespace-nowrap">
          LUXURIYA
        </h1>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">

        {/* LEFT IMAGES */}
        <div className="relative reveal-on-scroll group">
          <div className="relative z-10 w-[90%] md:w-[85%] h-[400px] md:h-[550px] overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.15)]">
            <img
              // SEO IMAGE OPTIMIZATION: Using a keyword-rich image URL or Alt text
              src="https://www.thewowstyle.com/wp-content/uploads/2015/02/Luxury-Nodern-Interior-Design-Skylab-Architecture-1600x1067-px-.jpg"
              alt="Luxury Living Room Interior Design in Delhi and Noida" 
              className="w-full h-full object-cover contrast-105 saturate-95"
            />
          </div>

          <div className="absolute -bottom-10 -right-2 md:-bottom-12 md:right-8 w-[60%] h-[250px] md:h-[300px] border-8 border-white shadow-2xl z-20 overflow-hidden reveal-on-scroll delay-200">
            <img
              src="https://i.ytimg.com/vi/50MAloxJsdE/maxresdefault.jpg"
              alt="Royal Home Renovation Project in Lucknow and Varanasi"
              className="w-full h-full object-cover"
            />
          </div>

          <div className="absolute top-8 -left-4 md:-left-8 w-full h-full border border-[#C6A75E]/30 -z-10"></div>

          {/* Experience Badge */}
          <div className="absolute top-6 -right-2 md:-right-10 z-30 hidden md:block">
            <div className="relative w-32 h-32 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-full shadow-lg">
              <div className="absolute inset-0 border border-dashed border-[#C6A75E] rounded-full animate-spin-text"></div>
              <div className="text-center">
                <span className="block text-3xl font-serif font-semibold text-[#C6A75E]">15+</span>
                <span className="text-[0.6rem] uppercase tracking-widest text-neutral-800 font-semibold">
                  Years Exp.
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT CONTENT - SEO FOCUSED */}
        <div className="pl-0 lg:pl-10 mt-16 lg:mt-0">
          <div className="reveal-on-scroll delay-100">
            <div className="flex items-center gap-4 mb-6">
              <span className="w-12 h-px bg-[#C6A75E]"></span>
              <span className="text-[#C6A75E] uppercase tracking-[0.2em] text-[11px] font-semibold">
                Serving Delhi • Lucknow • Varanasi
              </span>
            </div>

            {/* H2 HEADLINE: The most important tag for Local SEO */}
            <h2 className="font-['Playfair_Display'] text-[2.5rem] md:text-[3.8rem] leading-[1.1] text-neutral-900 mb-8">
              Best Interior Designers in <br />
              <span className="italic text-[#C6A75E] font-serif">
                Delhi, Lucknow & Varanasi
              </span>
            </h2>
          </div>

          {/* PARAGRAPH: Integrating location keywords naturally */}
          <p className="text-neutral-600 text-lg leading-relaxed mb-10 reveal-on-scroll delay-200 font-light max-w-lg">
            Luxuriya Homes brings world-class architecture and premium interior design to your doorstep. Whether it's a 
            <strong> modern villa in Delhi</strong>, a <strong>heritage home in Lucknow</strong>, or a 
            <strong> luxury apartment in Varanasi</strong>, we craft spaces that tell your unique story.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-4 mb-12 reveal-on-scroll delay-300">
            {[
              { title: 'Turnkey Projects', desc: 'Complete Home Renovation' },
              { title: 'Vastu Compliant', desc: 'Perfect for Indian Homes' }, // High intent keyword for Varanasi/Lucknow
              { title: 'Luxury Furniture', desc: 'Custom Manufacturing' },
              { title: 'Local Expertise', desc: 'Delhi, UP & NCR Region' },
            ].map((item, idx) => (
              <div key={idx} className="flex gap-4 items-start">
                <div className="mt-1 p-1 rounded-full bg-[#C6A75E]/10">
                  <CheckCircle size={18} className="text-[#C6A75E]" />
                </div>
                <div>
                  <h4 className="font-serif text-lg text-neutral-900">
                    {item.title}
                  </h4>
                  <p className="text-xs text-neutral-500 uppercase tracking-wide mt-1">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="reveal-on-scroll delay-300">
            <Link
              to="/contact"
              className="relative inline-flex items-center gap-4 px-12 py-5 border border-neutral-900 uppercase tracking-[0.25em] text-[11px] font-semibold text-neutral-900 overflow-hidden group"
            >
              <span className="absolute inset-0 bg-[#C6A75E] translate-y-full group-hover:translate-y-0 transition-transform duration-500"></span>
              <span className="relative z-10 group-hover:text-black transition-colors flex items-center gap-2">
                 Get Quote in Your City
              </span>
              <ArrowRight size={16} className="relative z-10" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WelcomeSection;